import { i18n } from "@/i18n";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import every from "lodash/every";
import uniq from "lodash/uniq";
import keyBy from "lodash/keyBy";
import orderBy from "lodash/orderBy";
import { surveyAPI } from "@/api";
import TeamHealthSurveyStatus from "@/constants/TeamHealthSurveyStatus";
import debugWrapper from "@/helpers/debugWrapper";
import moment from "moment";
import {
	HealthAssessmentResultsPublishedEvent,
	HealthAssessmentShowMyScoresEvent
} from "@/telemetry";

const MILLISECONDS = 1000;

const _state = {
	surveyTemplates: {},
	surveys: [],
	selectedSurveyId: undefined,
	isSavingUserResponse: false,
	isViewingUserScore: false,
	isWaitingOnSurveyPublish: false,
};

const _getters = {
	// state
	mostRecentSurveyName: state => {
		const createdDate = state.surveys[ 0 ]?.created;
		if ( createdDate ) {
			return moment( createdDate * MILLISECONDS ).format( "MMMM D" );
		}
	},
	availableSurveys: state => {
		return state.surveys.map( s => {
			return {
				displayDate: moment( s.created * MILLISECONDS ).format( "MMMM D, YYYY" ),
				id: s.id,
			};
		} );
	},
	daysSinceLastSurvey: state => {
		const created = state.surveys[ 0 ]?.created;
		if ( !created ) { return undefined; }
		const diff = moment().diff( moment( state.surveys[ 0 ].created * MILLISECONDS ), "days" );
		return diff;
	},
	surveys: state => state.surveys,
	selectedSurvey: state => {
		return state.surveys.find( s => s.id === state.selectedSurveyId ) || {};
	},
	activeSurvey: state => {
		return state.surveys.find( survey => survey.status === TeamHealthSurveyStatus.STARTED );
	},
	completedSurvey: state => {
		return state.surveys.find( survey => survey.status === TeamHealthSurveyStatus.COMPLETED );
	},
	selectedSurveyFinishedResponderIds: ( state, getters ) => {
		const { responders = {}, } = getters.selectedSurvey;
		return Object.keys( responders ).filter( personId => !!responders[ personId ] );
	},
	activeSurveyFinishedResponderIds: ( state, getters ) => {
		const { activeSurvey = {}, } = getters;
		const { responders = {}, } = activeSurvey;
		return Object.keys( responders ).filter( personId => !!responders[ personId ] );
	},
	selectedSurveyResults: state => {
		const surveyInstance = state.surveys.find( s => s.id === state.selectedSurveyId );
		if ( !surveyInstance ) {
			return [];
		}
		const surveyTemplate = state.surveyTemplates[ surveyInstance.surveyTemplateId ];
		return surveyTemplate.questions.map( q => {
			const question = {
				...q,
				...surveyInstance.result[ q.id ],
				title: i18n.t( `teamHealth.${ q.titleKey }` ),
				description: i18n.t( `teamHealth.${ q.descriptionKey }` ),
				priorResult: surveyInstance.priorSurveyResult[ q.id ]?.average,
			};
			if ( state.isViewingUserScore ) {
				question.myResponse = surveyInstance.myResponse[ q.id ];
			}
			return question;
		} );
	},
	selectedSurveyTemplate: ( state, getters ) => {
		return state.surveyTemplates[ getters.selectedSurvey.surveyTemplateId ];
	},

	// flags
	canCreateNewSurvey: state => {
		const unpublishedCount = state.surveys.reduce( ( accum, survey ) => {
			if ( survey.status !== TeamHealthSurveyStatus.PUBLISHED ) {
				accum += 1;
			}
			return accum;
		}, 0 );
		return unpublishedCount === 0;
	},
	hasActiveSurvey: state => {
		if ( state.surveys.length < 1 ) {
			return false;
		}
		return state.surveys[ 0 ].status === TeamHealthSurveyStatus.STARTED;
	},
	hasPriorPublishedSurvey: state => {
		if ( state.surveys.length <= 1 ) {
			return false;
		}
		const penultimateSurvey = state.surveys[ 1 ];
		return penultimateSurvey.status === TeamHealthSurveyStatus.PUBLISHED;
	},
	hasNoSurveys: state => state.surveys.length === 0,
	hasAllSurveyResponses: state => {
		if ( !state.selectedSurveyId ) {
			return false;
		}
		const survey = state.surveys.find( s => s.id === state.selectedSurveyId );
		return every( Object.values( survey.responders ), x => !!x );
	},
	isSurveyPublished: state => {
		if ( !state.selectedSurveyId ) {
			return false;
		}
		const survey = state.surveys.find( s => s.id === state.selectedSurveyId );
		return survey.status === TeamHealthSurveyStatus.PUBLISHED;
	},
	isSurveyReadyToPublish: state => {
		if ( !state.selectedSurveyId ) {
			return false;
		}
		const survey = state.surveys.find( s => s.id === state.selectedSurveyId );
		const unresponded = Object.values( survey.responders ).filter( x => !x );
		return survey.status === TeamHealthSurveyStatus.COMPLETED && unresponded.length === 0;
	},
	isViewingUserScore: state => state.isViewingUserScore,
	isWaitingOnMySurveyResponse: ( state, getters, rootState, rootGetters ) => {
		const personId = rootGetters[ "userProfile/personId" ];
		if ( !state.selectedSurveyId ) {
			return false;
		}
		const survey = state.surveys.find( s => s.id === state.selectedSurveyId );
		const hasRespondedToSurvey = survey.responders[ personId ];
		return !hasRespondedToSurvey;
	},
	isWaitingOnSurveyPublish: state => {
		if ( !state.selectedSurveyId ) {
			return false;
		}
		const survey = state.surveys.find( s => s.id === state.selectedSurveyId );
		return [
			TeamHealthSurveyStatus.STARTED,
			TeamHealthSurveyStatus.COMPLETED,
		].includes( survey.status );
	},
};

function findSelectedSurveyId( { surveys, } ) {
	let activeSurvey = surveys.find( s => [
		TeamHealthSurveyStatus.STARTED,
		TeamHealthSurveyStatus.COMPLETED,
	].includes( s.status ) );
	if ( !activeSurvey ) {
		activeSurvey = surveys[ 0 ];
	}
	return activeSurvey?.id;
}

const actions = debugWrapper( "teaming:store:teamHealth:actions", {
	async initTeamHealth( { commit, dispatch, }, { teamId, } ) {
		Nprogress.start();
		const surveys = orderBy(
			await surveyAPI.fetchTeamSurveys( { teamId, } ),
			[ "created", ], [ "desc", ]
		);
		const surveyTemplateIds = uniq( surveys.map( s => s.surveyTemplateId ) );
		const surveyTemplates = await Promise.all(
			surveyTemplateIds.map( templateId => {
				return surveyAPI.fetchSurveyTemplate( { templateId, } );
			} )
		);
		const mappedTemplates = keyBy( surveyTemplates, "id" );
		const selectedSurveyId = findSelectedSurveyId( { surveys, } );
		await dispatch( "toggleUserScoreVisibility", { isViewing: false, suppressTelemetry: true, } );
		commit( "surveysUpdated", { surveys, } );
		commit( "selectedSurveyIdUpdated", { selectedSurveyId, } );
		commit( "surveyTemplatesUpdated", { surveyTemplates: mappedTemplates, } );
		Nprogress.done();
	},
	async handleNewlyCreatedSurvey( { commit, state, }, surveyInstance ) {
		if ( !state.surveyTemplates[ surveyInstance.surveyTemplateId ] ) {
			const surveyTemplates = { ...state.surveyTemplates, };
			const newTemplate = await surveyAPI.fetchSurveyTemplate( { templateId: surveyInstance.surveyTemplateId, } );
			surveyTemplates[ newTemplate.id ] = newTemplate;
			commit( "surveyTemplatesUpdated", { surveyTemplates, } );
		}
		const surveys = [ surveyInstance, ...state.surveys, ];
		const selectedSurveyId = surveyInstance.id;
		commit( "surveysUpdated", { surveys, } );
		commit( "selectedSurveyIdUpdated", { selectedSurveyId, } );
	},
	async publishSurveyResults( { commit, state, rootGetters, }, { surveyInstanceId, } ) {
		Nprogress.start();
		commit( "surveyResponseSaveIndicatorChanged", true );
		try {
			const updatedSurvey = await surveyAPI.publishSurveyResults( { surveyInstanceId, } );
			const targetIndex = state.surveys.findIndex( s => s.id === updatedSurvey.id );
			const surveys = [ ...state.surveys, ];
			surveys.splice( targetIndex, 1, updatedSurvey );
			commit( "surveysUpdated", { surveys, } );

			const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
			const surveyInfo = rootGetters[ "teamHealth/availableSurveys" ].find( info => info.id === surveyInstanceId );
			const { name: teamName, } = activeTeam;
			const telemetryEvent = new HealthAssessmentResultsPublishedEvent( surveyInstanceId, surveyInfo.displayDate, teamName );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "teamHealth.TeamHealthSurvey_store_saveSurveyResponseError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		commit( "surveyResponseSaveIndicatorChanged", false );
		Nprogress.done();
	},
	selectSurveyInstance( { commit, }, { selectedSurveyId, } ) {
		commit( "selectedSurveyIdUpdated", { selectedSurveyId, } );
	},
	toggleUserScoreVisibility( { commit, getters, rootGetters, }, { isViewing, suppressTelemetry = false, } = { suppressTelemetry: false, } ) {
		commit( "userScoreVisibilityChanged", isViewing );

		if ( suppressTelemetry ) {
			return;
		}

		const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
		const { name: teamName, } = activeTeam;
		const surveyInstance = getters.selectedSurvey;
		if ( !surveyInstance?.id ) {
			return;
		}
		const surveyInfo = getters.availableSurveys.find( info => info.id === surveyInstance.id );
		const telemetryEvent = new HealthAssessmentShowMyScoresEvent( surveyInstance.id, surveyInfo.displayDate, teamName, isViewing );
		telemetryEvent.submit();
	},
}, { skipFirstArg: true, } );

const mutations = {
	surveysUpdated( state, { surveys, } ) {
		state.surveys = surveys;
	},
	selectedSurveyIdUpdated( state, { selectedSurveyId, } ) {
		state.selectedSurveyId = selectedSurveyId;
	},
	surveyTemplatesUpdated( state, { surveyTemplates, } ) {
		state.surveyTemplates = surveyTemplates;
	},
	userScoreVisibilityChanged( state, isViewing ) {
		state.isViewingUserScore = isViewing;
	},
	surveyResponseSaveIndicatorChanged( state, isSaving ) {
		state.isSavingUserResponse = isSaving;
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamHealth/index.js